import { Banner, Button, Empty, Loader, Surface, Table, Text } from '@cloudflare/kumo'
import { ArrowsClockwise, ClockCounterClockwise } from '@phosphor-icons/react'
import { PAYMENT_REVIEW_AUDIT_ACTIONS, type AdminAuditView } from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { adminApi, type Page } from '../api'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import { fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePage } from '../lib/use-page'

/** action 코드 → 사람이 읽는 말. 모르는 코드는 그대로 보여준다 (일반 라우트 기록은 "METHOD /path") */
const ACTION_LABEL: Record<string, string> = {
  'quote.notes': '견적 메모 저장',
  'quote.message': '견적 요청자에게 메시지',
  'user.credits.grant': '크레딧 지급',
  'user.credits.revoke': '크레딧 회수',
  // 결제·환불 기록의 확인 필요 행 처리 (target = 원장 행)
  [PAYMENT_REVIEW_AUDIT_ACTIONS.revoke]: '환불 확인 · 안 쓴 지급분 회수',
  [PAYMENT_REVIEW_AUDIT_ACTIONS.dismiss]: '환불 확인 · 회수 안 함',
  'user.message': '사용자에게 메시지',
  'user.delete': '계정 삭제',
  broadcast: '전체 공지 발송',
  'simulation.rerun': '시뮬레이션 재실행',
  'price.cache.delete': '재료 단가 삭제',
  'PUT /admin/settings': '설정 변경',
  'POST /admin/quotes/:id/offers': '공장 견적 추가',
  'PUT /admin/quotes/:id/offers/:offerId': '공장 견적 수정',
  'DELETE /admin/quotes/:id/offers/:offerId': '공장 견적 삭제',
  'PATCH /admin/quotes/:id': '견적 상태 변경',
  'POST /admin/admins': '어드민 추가',
  'DELETE /admin/admins/:email': '어드민 해제',
  'POST /admin/golden-set': '골든셋 추가',
  'POST /admin/golden-set/generate': '골든셋 검색 추가',
  'PATCH /admin/golden-set/:id': '골든셋 수정',
  'DELETE /admin/golden-set/:id': '골든셋 삭제',
  'PATCH /admin/community/posts/:id': '커뮤니티 글 수정',
  'DELETE /admin/community/posts/:id': '커뮤니티 글 삭제',
  // 신고 처리 (App Store 1.2) — 사용자 신고, 신고 알림의 처리 링크(report-link), 어드민 커뮤니티 화면
  'community.report': '커뮤니티 신고 접수',
  'community.post.delete': '커뮤니티 글 삭제 (신고 처리)',
  'community.comment.delete': '커뮤니티 댓글 삭제',
  'community.user.restrict': '커뮤니티 이용 제한',
  'community.user.unrestrict': '커뮤니티 이용 제한 해제',
  'POST /admin/prices/seed': '재료 단가 미리 채우기',
  // 완성 사례 (2단계 갤러리). 숨김·시작 허용·순위만 바뀐다
  'case.patch': '완성 사례 변경',
  'PATCH /admin/cases/:id': '완성 사례 변경',
  // 배정 운영자 권한 (C52)
  'access_grant.create': '견적 운영자 배정',
  'access_grant.revoke': '견적 운영자 배정 회수',
}

const show = (v: unknown) => (typeof v === 'object' ? JSON.stringify(v) : String(v))
const BEFORE_AFTER_KEYS = ['from', 'to', 'changes', 'beforeUnavailable', 'after']

/** 전후 값 (C04). 견적 상태 { from, to }, 설정 저장 { changes: { 경로: { before, after } } }, 읽기 실패 { beforeUnavailable, after } */
function beforeAfter(d: Record<string, unknown>): { before: string; after: string } | null {
  if ('from' in d && 'to' in d) return { before: show(d.from), after: show(d.to) }
  if (d.beforeUnavailable === true) return { before: '읽지 못함', after: show(d.after) }
  if (typeof d.changes !== 'object' || d.changes === null) return null
  const changes = Object.entries(d.changes as Record<string, { before: unknown; after: unknown }>)
  const side = (k: 'before' | 'after') => changes.map(([p, c]) => `${p} ${show(c[k])}`).join(' · ')
  return changes.length ? { before: side('before'), after: side('after') } : null
}

/** 전후 값이 있으면 '전 / 후' 두 줄과 나머지 한 줄, 없으면 예전처럼 한 줄. 빈 기록은 '—' */
function detailLines(d: Record<string, unknown> | null): string[] {
  if (!d) return ['—']
  const ba = beforeAfter(d)
  const rest = Object.entries(d)
    .filter(([k]) => k !== 'path' && !(ba && BEFORE_AFTER_KEYS.includes(k)))
    .map(([k, v]) => `${k}: ${show(v)}`)
    .join(' · ')
  return ba ? [`전: ${ba.before}`, `후: ${ba.after}`, rest].filter(Boolean) : [rest || '—']
}

/** 작업 기록 — 어드민에서 일어난 모든 변경 (누가 · 언제 · 무엇을). 조회는 기록하지 않는다 */
export function AuditPage() {
  const getToken = useGetToken()
  const { query, setPage } = usePage()
  const [data, setData] = useState<Page<AdminAuditView> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      setData(await adminApi.audit(getToken, query))
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken, query])

  useEffect(() => {
    void reload()
  }, [reload])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="작업 기록"
        description="어드민에서 일어난 모든 변경이에요. 누가 언제 무엇을 했는지 확인할 때 보세요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />
      <HelpPanel
        id="audit"
        steps={[
          <>
            최신순이에요. 견적 입력·설정 변경·크레딧 지급·삭제 같은 변경만 남고, 단순 조회는 남지
            않아요.
          </>,
          <>"대상" 열의 id 로 [견적 요청]·[사용자]·[시뮬레이션] 에서 해당 항목을 찾을 수 있어요.</>,
        ]}
        notes={['기록은 지울 수 없어요.']}
      />

      {error && <Banner variant="error" title="불러오지 못했어요" description={error} />}

      <Surface className="overflow-x-auto rounded-lg p-0">
        {data === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : data.items.length === 0 ? (
          <Empty
            icon={<ClockCounterClockwise size={40} className="text-kumo-inactive" />}
            title="아직 기록이 없어요"
            description="어드민에서 무언가를 바꾸면 여기에 남아요."
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>시각</Table.Head>
                <Table.Head>운영자</Table.Head>
                <Table.Head>작업</Table.Head>
                <Table.Head>대상</Table.Head>
                <Table.Head>상세</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.items.map((a) => (
                <Table.Row key={a.id}>
                  <Table.Cell className="whitespace-nowrap text-xs">
                    {fmtTime(a.createdAt)}
                  </Table.Cell>
                  <Table.Cell className="text-xs">{a.adminEmail}</Table.Cell>
                  <Table.Cell>
                    <div className="font-medium">{ACTION_LABEL[a.action] ?? a.action}</div>
                    {ACTION_LABEL[a.action] && (
                      <div className="text-kumo-subtle font-mono text-xs">{a.action}</div>
                    )}
                  </Table.Cell>
                  <Table.Cell className="max-w-48">
                    {a.targetId ? (
                      <div className="truncate font-mono text-xs" title={a.targetId}>
                        {a.targetType} · {a.targetId}
                      </div>
                    ) : (
                      <span className="text-kumo-subtle">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="max-w-xs">
                    <div
                      className="text-kumo-subtle text-xs"
                      title={detailLines(a.detail).join('\n')}
                    >
                      {detailLines(a.detail).map((line) => (
                        <div key={line} className="truncate">
                          {line}
                        </div>
                      ))}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>
      {data && (
        <TablePagination
          page={query.page}
          setPage={setPage}
          perPage={query.perPage}
          total={data.total}
        />
      )}
    </div>
  )
}
